import React, { createContext, useContext, useState, useCallback } from 'react'

interface GiftCardInfo {
  name: string
  email: string
  sender: string
  message: string
  faceImage: string
  value: string
}

interface GiftCardContextProps {
  giftCardInfo: GiftCardInfo
  setGiftCardInfo: (info: Partial<GiftCardInfo>) => void
  clearGiftCardInfo: () => void
}

const GiftCardContext = createContext<GiftCardContextProps | undefined>(
  undefined
)

const defaultGiftCardInfo: GiftCardInfo = {
  name: '',
  email: '',
  sender: '',
  message: '',
  faceImage: '',
  value: '',
}

export const GiftCardProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [giftCardInfo, setGiftCardInfoState] =
    useState<GiftCardInfo>(defaultGiftCardInfo)

  // Use useCallback para memoizar a função e evitar recriações
  const setGiftCardInfo = useCallback((info: Partial<GiftCardInfo>) => {
    setGiftCardInfoState((prev) => ({ ...prev, ...info }))
  }, [])

  // Use useCallback para memoizar a função
  const clearGiftCardInfo = useCallback(() => {
    setGiftCardInfoState(defaultGiftCardInfo)
  }, [])

  // Memoize o valor do contexto para evitar recriações desnecessárias
  const contextValue = React.useMemo(
    () => ({
      giftCardInfo,
      setGiftCardInfo,
      clearGiftCardInfo,
    }),
    [giftCardInfo, setGiftCardInfo, clearGiftCardInfo]
  )

  return (
    <GiftCardContext.Provider value={contextValue}>
      {children}
    </GiftCardContext.Provider>
  )
}

export function useGiftCardContext() {
  const context = useContext(GiftCardContext)

  if (!context) {
    throw new Error(
      'useGiftCardContext deve ser usado dentro de um GiftCardProvider'
    )
  }

  return context
}
